/*
 * pp-cart-sample.js : comportement des deux menus d'échantillons du panier
 * (snippets/pp-cart-sample.liquid), sur la page panier comme dans le tiroir.
 *
 * POURQUOI CE FICHIER N'EST PAS DANS LE SNIPPET
 * Le panier se redessine par « morph » (assets/morph.js) à chaque ajout ou
 * retrait d'article, et un <script> inséré de cette façon ne s'exécute jamais.
 * Tant que le script vivait dans le snippet, le module qui apparaissait dans le
 * tiroir après un premier ajout (panier vide au chargement de la page) restait
 * sans aucun écouteur : le choix de l'échantillon offert ne partait pas dans la
 * note de commande. Ce fichier est donc chargé sur toutes les pages par
 * sections/pp-cart-sample-js.liquid (groupe du pied de page), et ses écouteurs
 * sont délégués sur `document` : ils valent pour tout module, présent ou à venir.
 *
 * Les textes affichés viennent des attributs `data-*` du snippet, rendus par
 * Liquid dans la langue de la page : rien n'est traduit ici.
 */
import { CartAddEvent } from '@theme/events';

const routes = window.Theme?.routes || {};
const CART_ADD_URL = routes.cart_add_url || '/cart/add.js';
const CART_UPDATE_URL = routes.cart_update_url ? `${routes.cart_update_url}.js` : '/cart/update.js';
const CART_URL = routes.cart_url ? `${routes.cart_url}.js` : '/cart.js';
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Annonce au lecteur d'écran, dans la région `role="status"` du module. */
function announce(text) {
  const region = document.getElementById('pp-sample-status');
  if (!region || !text) return;
  // Vider d'abord : une région live ne relit pas deux textes identiques.
  region.textContent = '';
  setTimeout(() => {
    region.textContent = text;
  }, 50);
}

/* --- Échantillon offert : le titre choisi part dans la note de commande ---- */

async function saveFreeSample(select) {
  const note = select.options[select.selectedIndex]?.dataset.title || '';
  try {
    const response = await fetch(CART_UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note }),
    });
    // Une réponse d'erreur passe par ici comme une réussite : sans ce test, un
    // 422 affichait « ✓ Choix enregistré ».
    if (!response.ok) throw new Error(`cart/update ${response.status}`);
    select.dataset.savedTitle = note;
    const saved = document.getElementById('pp-free-saved');
    if (saved) {
      saved.classList.add('show');
      setTimeout(() => saved.classList.remove('show'), 2500);
      announce(saved.textContent.trim());
    }
  } catch {
    // Le thème n'a pas de chaîne d'erreur pour l'écriture d'une note : le menu
    // revient au dernier choix réellement enregistré, qui reste la vérité.
    const previous = select.dataset.savedTitle || '';
    const back = Array.from(select.options).find((option) => (option.dataset.title || '') === previous);
    select.value = back ? back.value : '';
  }
}

document.addEventListener('change', (event) => {
  const select = event.target;
  if (select instanceof HTMLSelectElement && select.id === 'pp-free-select') saveFreeSample(select);
});

/* --- Bandeau payant : défilement et ajout au panier ------------------------ */

function scrollTrack(arrow) {
  const track = document.getElementById(arrow.getAttribute('aria-controls') || '');
  if (!track) return;
  track.scrollBy({
    left: Number(arrow.dataset.ppScroll) * track.clientWidth * 0.85,
    behavior: reduceMotion.matches ? 'auto' : 'smooth',
  });
}

async function addSample(button) {
  const id = Number(button.dataset.ppSampleAdd);
  const original = Array.from(button.childNodes, (node) => node.cloneNode(true));
  const error = document.querySelector('[data-pp-paid-error]');
  // Mêmes sections que la fiche produit : le panier (page ou tiroir) se
  // redessine avec le HTML renvoyé, sans rechargement.
  const sections = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (el) => el.dataset.sectionId);

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  if (error) error.hidden = true;

  try {
    const response = await fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sections.join(',') }),
    });
    const data = await response.json();
    // Shopify répond 422 avec `status` et `description` (stock épuisé entre-temps,
    // quantité maximale atteinte) : c'est ce message, déjà traduit, qui est montré.
    if (!response.ok || data.status) throw new Error(data.description || data.message || '');

    const cart = await (await fetch(CART_URL)).json();
    const check = document.createElement('span');
    check.setAttribute('aria-hidden', 'true');
    check.textContent = '✓ ';
    button.replaceChildren(check, button.dataset.addedLabel || '');
    button.removeAttribute('aria-busy');
    announce(button.dataset.addedLabel);

    // Source différente de `product-form-component` : l'icône du panier prend
    // `itemCount` comme total, d'où le total relu juste avant.
    button.dispatchEvent(
      new CartAddEvent({}, String(id), {
        source: 'pp-cart-sample',
        itemCount: cart.item_count,
        variantId: String(id),
        sections: data.sections,
      })
    );
  } catch (err) {
    button.replaceChildren(...original);
    button.removeAttribute('aria-busy');
    button.disabled = false;
    if (error) {
      error.textContent = (err instanceof Error && err.message) || error.dataset.fallback || '';
      error.hidden = false;
      announce(error.textContent);
    }
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const arrow = event.target.closest('[data-pp-scroll]');
  if (arrow) return scrollTrack(arrow);
  const button = event.target.closest('[data-pp-sample-add]');
  if (button instanceof HTMLButtonElement && !button.disabled) addSample(button);
});